import React, { useEffect, useState } from "react";
import { Button, Form, Input as AntInput, notification, Typography, Progress } from "antd";
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useMutation } from "react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { confirmPasswordReset } from "../../../service/authService";
import styled from "styled-components";
import { AuthLayout } from "../auth-layout/AuthLayout";

const { Title, Text } = Typography;

const StyledTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 12px !important;
    color: #FEFFFC !important;
    font-weight: 500;
    text-align: center;
    font-size: clamp(2.5rem, 5vw, 4rem) !important;
  }
`;

const StyledSubtitle = styled(Text)`
  display: block;
  margin-bottom: 48px;
  color: #FEFFFC;
  font-size: 18px;
  text-align: center;
  opacity: 0.9;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 24px;

  .ant-form-item-label > label {
    color: #FEFFFC !important;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-input-affix-wrapper {
    background-color: #263e44 !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 8px !important;
    padding: 0 12px !important;
    height: 52px !important;
    display: flex;
    align-items: center;

    &:hover, &:focus, &-focused {
      border-color: #EBFED1 !important;
      background-color: #2c474d !important;
    }

    input.ant-input {
      background-color: transparent !important;
      border: none !important;
      height: 100% !important;
      padding: 0 !important;
      color: #FEFFFC !important;
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }
  }

  .ant-input-password-icon {
    color: rgba(255, 255, 255, 0.5) !important;
    &:hover {
      color: #FEFFFC !important;
    }
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
    color: #ff4d4f;
  }
`;

const StyledButton = styled(Button)``;

const PasswordRequirements = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;

  .requirements-title {
    font-weight: 600;
    color: #FEFFFC;
    margin-bottom: 12px;
    display: block;
    font-size: 14px;
  }

  .requirement-item {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: rgba(254, 255, 252, 0.6);
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .anticon {
      margin-right: 10px;
      font-size: 14px;
    }

    &.met {
      color: #EBFED1;
    }
  }
`;

const PasswordStrengthBar = styled.div`
  margin-top: -12px;
  margin-bottom: 24px;
`;

const SuccessCard = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #FEFFFC;

  .success-icon {
    font-size: 64px;
    color: #EBFED1;
    margin-bottom: 24px;
  }

  .success-title {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .success-description {
    font-size: 16px;
    opacity: 0.9;
    margin-bottom: 32px;
    line-height: 1.6;
  }
`;

const ErrorCard = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #FEFFFC;

  .error-icon {
    font-size: 64px;
    color: #ff4d4f;
    margin-bottom: 24px;
  }

  .error-title {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .error-description {
    font-size: 16px;
    opacity: 0.9;
    margin-bottom: 32px;
    line-height: 1.6;
  }
`;

interface PasswordRequirement {
  met: boolean;
  textKey: string;
}

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 25;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
  return Math.min(strength, 100);
};

const getPasswordRequirements = (password: string): PasswordRequirement[] => {
  return [
    { met: password.length >= 8, textKey: "passwordResetConfirmNew.requirementMinLength" },
    { met: /[a-z]/.test(password), textKey: "passwordResetConfirmNew.requirementLowercase" },
    { met: /[A-Z]/.test(password), textKey: "passwordResetConfirmNew.requirementUppercase" },
    { met: /[0-9]/.test(password), textKey: "passwordResetConfirmNew.requirementDigit" },
  ];
};

export const PasswordResetConfirmNew: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      notification.error({
        message: t("passwordResetConfirmNew.missingTokenMessage"),
        description: t("passwordResetConfirmNew.missingTokenDescription"),
        placement: "bottomRight",
      });
    }
  }, [token, t]);

  const { mutateAsync, isLoading } = useMutation(confirmPasswordReset, {
    onSuccess: () => {
      setResetSuccess(true);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || t("passwordResetConfirmNew.resetErrorFallback");
      const errorDetails = error?.response?.data?.details;

      notification.error({
        message: t("passwordResetConfirmNew.errorMessage"),
        description: (
          <div style={{ color: '#1E363C' }}>
            <div>{errorMessage}</div>
            {errorDetails && (
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {errorDetails.map((detail: string, idx: number) => (
                  <li key={idx}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        ),
        placement: "bottomRight",
      });
    },
  });

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  const handleSubmit = async (values: any) => {
    if (!token) return;

    await mutateAsync({
      token,
      newPassword: values.password,
    });
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (!tokenValid) {
    return (
      <AuthLayout maxWidth={540}>
        <ErrorCard>
          <CloseCircleOutlined className="error-icon" />
          <div className="error-title">{t("passwordResetConfirmNew.invalidLinkTitle")}</div>
          <div className="error-description">
            {t("passwordResetConfirmNew.invalidLinkDescriptionLine1")}
            <br />
            <br />
            {t("passwordResetConfirmNew.invalidLinkDescriptionLine2")}
          </div>
          <StyledButton block onClick={handleBackToLogin}>
            {t("passwordResetConfirmNew.backToLogin")}
          </StyledButton>
        </ErrorCard>
      </AuthLayout>
    );
  }

  if (resetSuccess) {
    return (
      <AuthLayout maxWidth={540}>
        <SuccessCard>
          <CheckCircleOutlined className="success-icon" />
          <div className="success-title">{t("passwordResetConfirmNew.successTitle")}</div>
          <div className="success-description">
            {t("passwordResetConfirmNew.successDescription")}
          </div>
          <StyledButton block onClick={handleBackToLogin}>
            {t("passwordResetConfirmNew.goToLogin")}
          </StyledButton>
        </SuccessCard>
      </AuthLayout>
    );
  }

  const requirements = getPasswordRequirements(password);

  return (
    <AuthLayout maxWidth={540}>
      <StyledTitle level={1}>
        {t("passwordResetConfirmNew.title")}
      </StyledTitle>
      <StyledSubtitle>
        {t("passwordResetConfirmNew.subtitle")}
      </StyledSubtitle>

      <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
        <StyledFormItem
          name="password"
          label={t("passwordResetConfirmNew.newPasswordLabel")}
          rules={[
            { required: true, message: t("passwordResetConfirmNew.passwordRequired") },
            { min: 8, message: t("passwordResetConfirmNew.passwordMinLength") },
            { max: 128, message: t("passwordResetConfirmNew.passwordMaxLength") },
          ]}
        >
          <AntInput.Password
            placeholder={t("passwordResetConfirmNew.newPasswordPlaceholder")}
            size="large"
            onChange={handlePasswordChange}
            autoComplete="new-password"
            autoFocus
          />
        </StyledFormItem >

        {password && (
          <>
            <PasswordStrengthBar>
              <Progress
                percent={passwordStrength}
                strokeColor={{
                  "0%": passwordStrength < 50 ? "#ff4d4f" : "#faad14",
                  "100%": passwordStrength >= 75 ? "#EBFED1" : "#faad14",
                }}
                showInfo={false}
                strokeWidth={8}
                style={{ marginBottom: 8 }}
              />
              <Text
                style={{
                  fontSize: 12,
                  color:
                    passwordStrength < 50
                      ? "#ff4d4f"
                      : passwordStrength < 75
                        ? "#faad14"
                        : "#EBFED1",
                }}
              >
                {t("passwordResetConfirmNew.passwordStrengthLabel")}{" "}
                {passwordStrength < 50
                  ? t("passwordResetConfirmNew.passwordStrengthWeak")
                  : passwordStrength < 75
                    ? t("passwordResetConfirmNew.passwordStrengthMedium")
                    : t("passwordResetConfirmNew.passwordStrengthStrong")}
              </Text>
            </PasswordStrengthBar>

            <PasswordRequirements>
              <span className="requirements-title">{t("passwordResetConfirmNew.requirementsTitle")}</span>
              {requirements.map((req, idx) => (
                <div key={idx} className={`requirement-item ${req.met ? "met" : "not-met"}`}>
                  {req.met ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  {t(req.textKey)}
                </div>
              ))}
            </PasswordRequirements>
          </>
        )}

        <StyledFormItem
          name="confirmPassword"
          label={t("passwordResetConfirmNew.confirmPasswordLabel")}
          dependencies={["password"]}
          rules={[
            { required: true, message: t("passwordResetConfirmNew.confirmPasswordRequired") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t("passwordResetConfirmNew.passwordsNotIdentical")));
              },
            }),
          ]}
        >
          <AntInput.Password
            placeholder={t("passwordResetConfirmNew.confirmPasswordPlaceholder")}
            size="large"
            autoComplete="new-password"
          />
        </StyledFormItem >

        <Form.Item style={{ marginBottom: 0 }}>
          <StyledButton type="primary" htmlType="submit" loading={isLoading} block>
            {isLoading ? t("passwordResetConfirmNew.resetting") : t("passwordResetConfirmNew.resetButton")}
          </StyledButton>
        </Form.Item>
      </Form >
    </AuthLayout >
  );
};
